import { useCallback, useState } from "react";
import { MultiPolygon } from "@turf/helpers";
import { saveSitesBulk } from "../utils";
import { SaveSiteLayer } from "react-migration/domains/sites/typings";
import { LogEventCreatedSiteEventLocation } from "react-migration/lib/util/logEventV2";
import { refreshSiteGeometryStore } from "src/js/stores/siteGeometry/actions";
import { Stores, useGlobalState, useTranslation } from "react-migration/lib";
import { logEventV2 } from "react-migration/lib/util/logEventV2";

type UseSaveSitesBulkParams = {
  geometries: MultiPolygon[];
  layer: SaveSiteLayer;
  source: LogEventCreatedSiteEventLocation;
  selectedStageId: string;
};

export const useSaveSitesBulk = ({
  geometries,
  layer,
  source,
  selectedStageId,
}: UseSaveSitesBulkParams) => {
  const [isLoading, setIsLoading] = useState(false);
  const [{ googleMap }] = useGlobalState(Stores.map);
  const { t } = useTranslation();
  const [{ savedResults, searchId }, { setResultsSaved, setSavedResultsError }] = useGlobalState(
    Stores.search
  );

  const handleSaveBulk = useCallback(async () => {
    setIsLoading(true);
    let saveErrorOccurred = false;
    let sitesSavedCount = 0;
    try {
      setResultsSaved(false);
      setSavedResultsError("");
      const sitesSaved = await saveSitesBulk({
        sites: geometries
          .filter((geo) => geo)
          .map((geometry) => ({
            source: { layer },
            geometry: geometry,
          })),
        layer,
        source,
        stageId: selectedStageId,
      });
      setResultsSaved(true);
      sitesSavedCount = sitesSaved.length;
    } catch (err) {
      saveErrorOccurred = true;
      setSavedResultsError(t("sidebar.search.results.all_sites_saved_error"));
      console.error(err);
    }

    if (!saveErrorOccurred) {
      const createdSiteProperties = { source, eventLocation: source, sitesSavedCount, searchId };
      if (source === "SearchResultItem") {
        createdSiteProperties["searchId"] = searchId;
      }

      logEventV2({ name: "Created sites", properties: createdSiteProperties });
    }

    setIsLoading(false);
    refreshSiteGeometryStore(googleMap!);
  }, [
    geometries,
    googleMap,
    layer,
    selectedStageId,
    source,
    setResultsSaved,
    setSavedResultsError,
    searchId,
    t,
  ]);

  return {
    bulkSaveProcessDone: savedResults,
    isLoading,
    handleSaveBulk,
  };
};
